import { WorkerChurnRisk, WorkerChurnRiskV2 } from './health-dash'
import { RequirementType } from './requirements'
import type { IntercomTicket } from './worker-support'
import { WorkerPreferences } from './worker-preferences'
import {
  AccountApprovalStatus,
  AdjudicationStatus,
  BackgroundCheckAssessment,
  BackgroundCheckStatus,
  PhoneNumberStatus,
} from './account-status'
import { TestAccountType } from './base'
import { RecurringWorkerShiftWithSchedules } from './recurring-worker-shift'
import { Strike } from './worker-strikes'
import { TierLevel } from './worker-tiers'

export interface Worker {
  // Note: use uid for the id of worker, workerId has been deprecated and don't use it
  uid: string
  id?: string
  workerId: string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  zipCode: string
  street1?: string
  street2?: string
  city?: string
  state?: string
  password?: string
  photoUrl?: string
  regionId: string
  stripeAccountId?: string
  zealEmployeeId?: string
  zealOnboardingLink?: string
  zealOnboardingExpiresAt?: string
  workBrightEmployeeId?: string
  testAccountType?: TestAccountType
  referredBy?: string
  referralCode: string
  acquisitionSource?: string
  createdAt: Date
  zipCodeGeohash: string
  languages: string[]
  accessToVehicle?: boolean
  workPreferences?: WorkPreferences // TODO (jikez) - legacy field can be removed after worker preferences are fully migrated
  schedulePreferences?: SchedulePreferences // TODO (jikez) - legacy field can be removed after worker preferences are fully migrated
  lastActiveAt?: Date
  lastActiveAtApp?: Date
  lastActiveAtComms?: Date
  reportedGender?: WorkerGender
  opsReportedGender?: WorkerGender
  isProfilePhotoValid?: boolean
  strikes?: Strike[]
  workerChurnRisk?: WorkerChurnRisk[]
  workerChurnRiskV2?: WorkerChurnRiskV2[]
  intercomTickets?: IntercomTicket[]
  operatorAiConsent?: boolean
  workerPreferences?: WorkerPreferences[]
}

export enum HoursPerWeek {
  MoreThanThirty = 'MORE_THAN_THIRTY',
  TwentyToThirty = 'TWENTY_TO_THIRTY',
  LessThanTwenty = 'LESS_THAN_TWENTY',
  NotSure = 'NOT_SURE',
  DidNotAnswer = 'DID_NOT_ANSWER',
}

export enum ShiftConsistency {
  OneOff = 'ONE_OFF',
  Consistent = 'CONSISTENT',
  DidNotAnswer = 'DID_NOT_ANSWER',
}

export enum Transportation {
  DRIVE = 'DRIVE',
  PUBLIC = 'PUBLIC',
  CARPOOL = 'CARPOOL',
  SHARE = 'SHARE',
  DidNotAnswer = 'DID_NOT_ANSWER',
}

export enum WorkerGender {
  Male = 'MALE',
  Female = 'FEMALE',
  PreferNotToSay = 'PREFER_NOT_TO_SAY',
  Other = 'OTHER',
}

export type ScheduleTimes = {
  daytime: boolean
  evening: boolean
  overnight: boolean
}

export type SchedulePreferences = {
  weekday: ScheduleTimes
  weekend: ScheduleTimes
}

export type WorkPreferences = {
  hoursPerWeek?: HoursPerWeek
  shiftConsistency?: ShiftConsistency
  transportation?: Transportation
}

export type IncludeInExploreResult = {
  included: boolean
  reason: string
}

export type RecurringEligibilityCriteria = {
  recurringSlotAvailable: boolean
  workerIsAvailable: boolean
  workerOnRecurringShift?: RecurringWorkerShiftWithSchedules
  conflictingRecurringShiftIds: {
    shiftRequestId: string
    originalStartTime: Date
  }[]
  conflictingShiftIds: string[]
}

export type FailedRequirement = {
  id: string
  name: string
  requirementType: RequirementType
}

export type WorkerShiftEligibility = {
  workerId: string
  shiftId: string
  reliability: number
  experienceLevel: ExperienceLevel
  workerTierLevel: TierLevel
  eligibilityCriteria: EligibilityCriteria
  conflictingShiftIds?: string[]
  attributesFailedEligibility: string[]
  workerAlreadySignedUpForShift: boolean
  workerAlreadySignedUpForPaidBackup: boolean
  recurringEligibilityCriteria?: RecurringEligibilityCriteria
  failedRequirements?: Record<string, FailedRequirement[]>
  shiftsWorked?: number
  isFavorite?: boolean
  includeInExplore?: IncludeInExploreResult
  waitlistOnly?: boolean
  canJoinAsPaidBackup?: boolean
}

export type ShiftRequestEligibility = {
  workerId: string
  shiftRequestId: string
  reliability: number
  experienceLevel: ExperienceLevel
  workerTierLevel: TierLevel
  eligibilityCriteria: EligibilityCriteria
  conflictingShiftIds?: string[]
  workerAlreadySignedUpForShift: boolean
  workerAlreadySignedUpForPaidBackup: boolean
  recurringEligibilityCriteria?: RecurringEligibilityCriteria
  failedRequirements?: Record<string, FailedRequirement[]>
}

export type CheckScheduleEligibilityResponse = {
  eligibilities: ShiftRequestEligibility[]
}

export enum ExperienceLevel {
  Unproven = 'UNPROVEN',
  Proven = 'PROVEN',
}

export type GenericEligibilityCriteria = Record<string, boolean>

export type EligibilityCriteria = {
  readonly shiftStatusOk: boolean
  readonly shiftAllowsSignups: boolean
  readonly accountStatusOk: boolean
  readonly workerIsCertified: boolean
  readonly workerHasAttributes: boolean
  readonly workerHasNoRevokedAttributes: boolean
  readonly shiftIsNotFull: boolean
  readonly workerIsNotBlocked: boolean
  readonly workerIsNotIneligible: boolean
  readonly workerPassesFavoritesOnly: boolean
  readonly workerIsAvailable: boolean
  readonly workerPassesResumeUploadRequired: boolean
  readonly workerPassesRegion: boolean
  readonly workerPassesFavoritesFirst: boolean
  readonly companyIsApproved: boolean
  readonly workerPassesUnprovenThreshold: boolean
  readonly workerPassesMinimumAgeRequirement: boolean
  readonly workerPassesUnprovenTodoLimit: boolean
  readonly workerPassesAcceptedTier: boolean
  readonly workerPassesExtraBGCRequirement: boolean
  readonly workerPassesForwardFill: boolean
  readonly workerPassesInvitedOnly: boolean
  readonly workerPassesInvitedFirst: boolean
  readonly shiftPassesNoBackfill: boolean
  readonly workerShiftIsNotBizCancelled: boolean
  readonly workerShiftIsNotOpsCancelled: boolean
  readonly workerPassesOpsBGCRequirement: boolean
  readonly workerPassesBGCRequirement: boolean
  readonly workerMeetsW9Requirement: boolean
  readonly workerMeetsI9Requirement: boolean
  readonly workerPassesGenderPreference: boolean
  readonly workerPassesExperiencedOnly: boolean
  readonly workerPassesExperiencedFirst: boolean
  readonly workerPassesInexperiencedOnly: boolean
  readonly hasMatchingEmploymentType: boolean
}

export type BasicOpsWorkerDetails = {
  id: string
  firstName: string
  lastName: string
  phoneNumber: string
  reportedGender?: WorkerGender
  opsReportedGender?: WorkerGender
  photoUrl?: string
  zipCode?: string
  accountApprovalStatus?: AccountApprovalStatus
  tierLevel?: TierLevel
  completedShifts?: number
  transportation?: Transportation
  isFirstTimeWorkerShiftWithCompany?: boolean
  backgroundCheckStatus?: BackgroundCheckStatus
  payoutsEnabled?: boolean
  payoutsDisabledDeadline?: Date
  phoneNumberStatus?: PhoneNumberStatus
  backgroundCheckAdjudication?: AdjudicationStatus
  backgroundCheckAssessment?: BackgroundCheckAssessment
  referredBy?: string
  referredByWorkerId?: string
  referredByWorkerName?: string
  referredByWorkerTierLevel?: string
}

export const workerGenderMenuItems: Array<{
  value: WorkerGender
  label: string
}> = [
  { value: WorkerGender.Male, label: 'Male' },
  { value: WorkerGender.Female, label: 'Female' },
  { value: WorkerGender.PreferNotToSay, label: 'Prefer Not To Say' },
  { value: WorkerGender.Other, label: 'Other' },
] as const
